import React, { useEffect, useState } from 'react';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import StradaSpinner from 'shared-components/components/StradaSpinner';
import { useSelector } from 'react-redux';
import type { RootState } from 'mainStore';
import {
  useLocation,
} from 'react-router-dom';

export default function AmountBudgetedBar(): JSX.Element {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const [isFocused, setIsFocused] = useState(false);
  const [amount, setAmount] = useState(NaN);
  const [updating, setUpdating] = useState(false);
  // const sideSheetData = useSelector((state: RootState) => state.workspaces.sideSheetData.sideSheetData);
  const singleSideSheetData = useSelector((state: RootState) => state.workspaces.sideSheetData.singleSideSheetData);

  useEffect(() => {
    if (singleSideSheetData !== null) {
      if (singleSideSheetData.amount_budget !== null) {
        setAmount(singleSideSheetData.amount_budget);
      } else {
        setAmount(NaN);
      }
    } else {
      setAmount(NaN);
    }
  }, [singleSideSheetData]);

  const { mutate: updateAmountBudget } = useMutation(async () => axios({
    url: `/api/budget-calendar/event/${singleSideSheetData?.id}/`,
    method: 'PATCH',
    data: {
      amount_budget: Number.isNaN(Number(amount)) ? 0 : amount,
    },
  }), {
    onSuccess: async (res): Promise<void> => {
      setUpdating(false);
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      res.data.detail.message === 'updated' && enqueueSnackbar('Amount budgeted updated successfully');
      if (!location.pathname.includes('dashboard')) {
        await queryClient.invalidateQueries('get-single-sidesheet').then();
        await queryClient.invalidateQueries('sidesheet/get-events').then();
      } else {
        await queryClient.invalidateQueries('get-single-sidesheet').then();
        await queryClient.invalidateQueries('others-events').then();
        await queryClient.invalidateQueries('prioritized-events').then();
        await queryClient.invalidateQueries('user-assigned-events').then();
        await queryClient.invalidateQueries('allevents/get-events').then();
        await queryClient.invalidateQueries('user-created-events').then();
      }
    },
    onError: (): void => {
      setUpdating(false);
      setAmount(typeof singleSideSheetData?.amount_budget === 'number' ? singleSideSheetData.amount_budget : 0);
      enqueueSnackbar('Updation failed.', {
        variant: 'error',
        content: (key, message) => (
          <div className='text-white bg-danger ps-4 pe-5 py-3'>
            {message}
          </div>
        ),
      });
    },
  });

  const handleEnterPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      if (singleSideSheetData !== null && singleSideSheetData.amount_budget !== amount) {
        if (!(singleSideSheetData.amount_budget === null && Number.isNaN(amount))) {
          updateAmountBudget();
          setUpdating(true);
        }
      }
    }
  };
  const handleBlur = (): void => {
    setIsFocused(false);
    if (singleSideSheetData !== null && singleSideSheetData.amount_budget !== amount) {
      if (!(singleSideSheetData.amount_budget === null && Number.isNaN(amount))) {
        updateAmountBudget();
        setUpdating(true);
      }
    }
  };

  const handleConditionalText = (): JSX.Element => {
    if (isFocused || !Number.isNaN(amount)) {
      return <>$</>;
    }
    return <HorizontalRuleIcon />;
  };
  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.value.length < 11) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      setAmount(e.target.valueAsNumber); handleConditionalText();
    }
  };

  return (
    <div className='assignee-sheet-bar'>
      <h6 className='side-sheet-side-label'> Amount Budgeted </h6>
      <div className='assignee-sheet-popover'>
        <div className='popover-btn' aria-hidden='true'>
          {updating && amount ? <StradaSpinner open={updating} message='Updating' /> : (
            <div className='input-bar'>
              {handleConditionalText()}
              <input
                value={String(amount)}
                onChange={(e): void => { handleValueChange(e); }}
                onFocus={(): void => { setIsFocused(true); }}
                onBlur={(): void => { handleBlur(); }}
                onKeyPress={handleEnterPress}
                type='number'
                min='0'
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
